import { useState } from "react";
import { ExternalLink, FileSpreadsheet, FileText, Download, Link2, Presentation, Youtube, HardDrive, Globe, X } from "lucide-react";
import { PdfThumbnail } from "@/components/PdfThumbnail";
import { RichText } from "@/components/RichText";
import { Wrap, VideoPlayer, isEmbeddableVideo, SectionHead } from "@/website/sections/Basic";

const ext = (u = "") => (u.split("?")[0].split(".").pop() || "").toLowerCase();
export const fileKind = (u = "") => {
  const e = ext(u);
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(e)) return "image";
  if (["mp4", "webm", "mov"].includes(e) || isEmbeddableVideo(u)) return "video";
  if (["mp3", "wav", "m4a", "ogg", "aac"].includes(e)) return "audio";
  if (e === "pdf") return "pdf";
  if (["doc", "docx", "ppt", "pptx", "xls", "xlsx", "odt", "ods", "odp", "rtf"].includes(e)) return "office";
  return "file";
};
const absolute = (u) => (u.startsWith("/") ? `${window.location.origin}${u}` : u);
const OFFICE_ICON = { ppt: Presentation, pptx: Presentation, odp: Presentation, xls: FileSpreadsheet, xlsx: FileSpreadsheet, ods: FileSpreadsheet };

const Viewer = ({ item, onClose }) => (
  <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col p-3 sm:p-6" onClick={onClose} data-testid="files-viewer">
    <div className="flex justify-end gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
      <a href={item.url} download className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm inline-flex items-center gap-2" data-testid="files-viewer-download"><Download className="w-4 h-4" />Download</a>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm inline-flex items-center gap-2"><ExternalLink className="w-4 h-4" />New tab</a>
      <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Close" data-testid="files-viewer-close"><X className="w-5 h-5" /></button>
    </div>
    <div className="flex-1 flex items-center justify-center min-h-0" onClick={(e) => e.stopPropagation()}>
      {item.kind === "image" ? <img src={item.url} alt={item.title || ""} className="max-w-full max-h-full object-contain rounded-lg" />
        : <iframe src={item.url} title={item.title || "Document"} className="w-full h-full max-w-5xl bg-white rounded-lg" />}
    </div>
  </div>
);

const FileTile = ({ item, onOpen, testId }) => {
  const k = fileKind(item.url);
  const it = { ...item, kind: k };
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const open = () => (k === "pdf" && ios ? window.open(item.url, "_blank", "noopener") : onOpen(it));
  const meta = (
    <div className="p-4">
      <p className="font-semibold text-slate-900">{item.title || item.url.split("/").pop()}</p>
      {item.description && <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>}
    </div>
  );
  const shell = "rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm";
  if (k === "video") return <div className={shell} data-testid={testId}><VideoPlayer url={item.url} testId={`${testId}-player`} />{meta}</div>;
  if (k === "audio") return <div className={shell} data-testid={testId}>{meta}<audio controls preload="none" src={item.url} className="w-full px-4 pb-4" data-testid={`${testId}-audio`} /></div>;
  if (k === "image" || k === "pdf") return (
    <button onClick={open} className={`${shell} text-left group transition-[box-shadow,transform] hover:shadow-md hover:-translate-y-0.5`} data-testid={testId}>
      <div className="h-44 bg-slate-100 overflow-hidden">
        {k === "image" ? <img src={item.url} alt={item.title || ""} loading="lazy" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
          : <PdfThumbnail url={item.url} filename={item.title} className="w-full h-full" />}
      </div>
      {meta}
    </button>
  );
  const Icon = k === "office" ? (OFFICE_ICON[ext(item.url)] || FileText) : FileText;
  return (
    <div className={`${shell} flex flex-col`} data-testid={testId}>
      <div className="h-24 flex items-center justify-center bg-slate-50"><Icon className="w-10 h-10 text-[var(--site-primary)]" /><span className="ml-2 text-xs font-bold uppercase text-slate-400">{ext(item.url)}</span></div>
      {meta}
      <div className="px-4 pb-4 mt-auto flex flex-wrap gap-2">
        <a href={item.url} download className="text-sm font-semibold text-[var(--site-primary)] inline-flex items-center gap-1" data-testid={`${testId}-download`}><Download className="w-4 h-4" />Download</a>
        {k === "office" && <a href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(absolute(item.url))}`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1" data-testid={`${testId}-office`}><ExternalLink className="w-4 h-4" />Open online</a>}
      </div>
    </div>
  );
};

export const FilesSection = ({ d, testId }) => {
  const [open, setOpen] = useState(null);
  return (
    <Wrap testId={testId}>
      <SectionHead heading={d.heading} subheading={d.subheading} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(d.items || []).filter((i) => i.url).map((it, i) => <FileTile key={i} item={it} onOpen={setOpen} testId={`${testId}-file-${i}`} />)}
      </div>
      {open && <Viewer item={open} onClose={() => setOpen(null)} />}
    </Wrap>
  );
};

export const AudioSection = ({ d, testId }) => (
  <Wrap testId={testId} narrow>
    {d.heading && <h2 className="text-2xl font-extrabold font-['Manrope'] mb-4">{d.heading}</h2>}
    {d.url && <audio controls preload="none" src={d.url} className="w-full" data-testid={`${testId}-audio`} />}
    {d.caption && <RichText html={d.caption} className="mt-3 text-slate-600" />}
  </Wrap>
);

const linkIcon = (u = "") => (/youtube\.com|youtu\.be/.test(u) ? Youtube : /drive\.google|docs\.google/.test(u) ? HardDrive : /^https?:/.test(u) ? Globe : Link2);

export const LinksSection = ({ d, testId }) => (
  <Wrap testId={testId} narrow>
    <SectionHead heading={d.heading} subheading={d.subheading} />
    <div className="space-y-3">
      {(d.items || []).filter((l) => l.url).map((l, i) => {
        const Icon = linkIcon(l.url);
        const ext = /^https?:/.test(l.url);
        return (
          <a key={i} href={l.url} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined}
            className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition-[box-shadow,border-color] hover:shadow-md hover:border-[var(--site-primary)]" data-testid={`${testId}-link-${i}`}>
            <span className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[var(--site-primary)]" /></span>
            <span className="flex-1 min-w-0"><span className="block font-semibold text-slate-900">{l.label || l.url}</span>{l.description && <span className="block text-sm text-slate-500">{l.description}</span>}</span>
            {ext && <ExternalLink className="w-4 h-4 text-slate-400" />}
          </a>
        );
      })}
    </div>
  </Wrap>
);
